import React, { useState, useEffect } from 'react';
import {
  Container, Title, Text, TextInput, Textarea, Button, Grid,
  createStyles, Paper, Group, ThemeIcon, LoadingOverlay, Notification, Stack
} from '@mantine/core';
import { IconBrandWhatsapp, IconBrandInstagram, IconMail, IconSend, IconMapPin, IconCheck, IconX } from '@tabler/icons-react';
import axios from 'axios';

const useStyles = createStyles((theme) => ({
  wrapper: {
    paddingTop: 80,
    paddingBottom: 80,
    minHeight: '100vh',
    backgroundColor: '#050505',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
    overflowX: 'hidden',
  },

  title: {
    color: theme.white,
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    fontSize: 44,
    lineHeight: 1.2,
    fontWeight: 900,
    marginBottom: theme.spacing.xl,
    textAlign: 'center',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 32,
    },
  },

  subtitle: {
    color: theme.colors.gray[4],
    textAlign: 'center',
    marginBottom: theme.spacing.xl * 2,
    maxWidth: 600,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  glassPanel: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing.xl * 1.5,
    borderRadius: theme.radius.lg,
    height: '100%',
    color: theme.white,
    position: 'relative',
    padding: '20px',
    marginTop: '60px'
  },

  contactIcon: {
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    }
  },

  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.white,
    '&:focus': {
      borderColor: theme.colors.cyan[6],
    },
    '& input, & textarea': {
      color: theme.white,
    },
    '& input::placeholder, & textarea::placeholder': {
      color: theme.colors.gray[5],
    }
  },

  label: {
    color: theme.colors.gray[3],
  }
}));

export default function Contact() {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await axios.post('https://backend.cdp3d.com/contact', formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.wrapper}>
      <Container size="lg">
        <Title className={classes.title}>Get in Touch</Title>
        <Text className={classes.subtitle}>
          Have a question about our services or need a custom quote? We'd love to hear from you.
          Fill out the form below or reach us through our social channels.
        </Text>

        <Grid gutter="xl">
          <Grid.Col md={6}>
            <Paper className={classes.glassPanel}>
              <Title order={3} mb="lg" color="white">Contact Information</Title>

              <Stack spacing="xl">
                <Group>
                  <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'cyan', to: 'blue' }}>
                    <IconBrandWhatsapp size={22} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={700}>WhatsApp / Phone</Text>
                    <Text color="white" weight={500}>9354061080</Text>
                  </div>
                </Group>

                <Group>
                  <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'pink', to: 'red' }}>
                    <IconBrandInstagram size={22} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={700}>Instagram</Text>
                    <Text color="white" weight={500} component="a" href="https://www.instagram.com/cdp_3d" target="_blank" style={{ textDecoration: 'none' }}>
                      @cdp_3d
                    </Text>
                  </div>
                </Group>

                <Group>
                  <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'orange', to: 'yellow' }}>
                    <IconMail size={22} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={700}>Email</Text>
                    <Text color="white" weight={500} component="a" href="mailto:cdp.threed@gmail.com" style={{ textDecoration: 'none' }}>
                      cdp.threed@gmail.com
                    </Text>
                  </div>
                </Group>

                <Group>
                  <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'teal', to: 'lime' }}>
                    <IconMapPin size={22} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={700}>Address</Text>
                    <Text color="white" weight={500}>
                      184-A, Gali No. 3, Padam Nagar,<br />Delhi-110007
                    </Text>
                  </div>
                </Group>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col md={6}>
            <Paper className={classes.glassPanel}>
              <LoadingOverlay visible={loading} overlayBlur={2} />
              <Title order={3} mb="lg" color="white">Send us a Message</Title>

              <form onSubmit={handleSubmit}>
                <Stack spacing="md">
                  <Grid gutter="md">
                    <Grid.Col span={6}>
                      <TextInput
                        label="Name"
                        placeholder="Your name"
                        classNames={{ input: classes.input, label: classes.label }}
                        required
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        label="Phone"
                        placeholder="Your phone"
                        classNames={{ input: classes.input, label: classes.label }}
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Grid.Col>
                  </Grid>

                  <TextInput
                    label="Email"
                    placeholder="your@email.com"
                    required
                    classNames={{ input: classes.input, label: classes.label }}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />

                  <TextInput
                    label="Subject"
                    placeholder="What is this regarding?"
                    classNames={{ input: classes.input, label: classes.label }}
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  />

                  <Textarea
                    label="Message"
                    placeholder="Your message here..."
                    minRows={4}
                    required
                    classNames={{ input: classes.input, label: classes.label }}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                  />

                  {success && (
                    <Notification icon={<IconCheck size={18} />} color="teal" title="Message Sent" onClose={() => setSuccess(false)}>
                      Thanks for reaching out! We'll get back to you soon.
                    </Notification>
                  )}

                  {error && (
                    <Notification icon={<IconX size={18} />} color="red" title="Error" onClose={() => setError(null)}>
                      {error}
                    </Notification>
                  )}

                  <Button
                    type="submit"
                    size="md"
                    fullWidth
                    variant="gradient"
                    gradient={{ from: 'cyan', to: 'indigo' }}
                    rightIcon={<IconSend size={18} />}
                  >
                    Send Message
                  </Button>
                </Stack>
              </form>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>
    </div>
  );
}